import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { useQuadro } from "@/hooks/useQuadro";
import { etapaInicial, rotuloEtapa } from "@shared/etapas";
import { cn } from "@/lib/utils";

// ─── Tokens ───────────────────────────────────────────────────────────────────

type Rede = "instagram" | "tiktok" | "youtube" | "linkedin" | "twitter";

const STATUS_INFO: Record<string, { label: string; color: string; bg: string }> = {
  nova:        { label: "Nova",        color: "#0A0A0A", bg: "#EDEBE6" },
  em_analise:  { label: "Em análise",  color: "#52525B", bg: "#FEF3C7" },
  aprovada:    { label: "Aprovada",    color: "#059669", bg: "#D1FAE5" },
  convertida:  { label: "Convertida",  color: "#0891B2", bg: "#E0F7FA" },
  descartada:  { label: "Descartada",  color: "#EF4444", bg: "#FEE2E2" },
};
const STATUS_ORDER = ["nova", "em_analise", "aprovada", "convertida", "descartada"];

const REDE_INFO: Record<Rede, { label: string; icon: string }> = {
  instagram: { label: "Instagram", icon: "" },
  tiktok:    { label: "TikTok",    icon: "" },
  youtube:   { label: "YouTube",   icon: "▶️" },
  linkedin:  { label: "LinkedIn",  icon: "" },
  twitter:   { label: "Twitter/X", icon: "𝕏" },
};

const LINHAS_EDITORIAIS = [
  { value: "L1 — Compliance & Controle",       color: "#0A0A0A", bg: "#EDEBE6" },
  { value: "L2 — Orquestração sem Milagre",    color: "#2563EB", bg: "#DBEAFE" },
  { value: "L3 — Dinheiro na Mesa",            color: "#10B981", bg: "#D1FAE5" },
  { value: "L4 — Bastidores da Operação",      color: "#52525B", bg: "#FEF3C7" },
  { value: "L5 — Maturidade e Visão",          color: "#8B5CF6", bg: "#EDE9FE" },
  { value: "L6 — Tendências e Provocações",    color: "#EC4899", bg: "#FDE8F5" },
];
function linhaColor(val: string | null | undefined): { color: string; bg: string } {
  const found = LINHAS_EDITORIAIS.find(l => val?.startsWith(l.value.split(" ")[0]));
  return found ?? { color: "#262626", bg: "#F5F4F1" };
}

// HU-068: origem da ideia
const ORIGENS = [
  { value: "manual",      label: "Manual",      icon: "️" },
  { value: "ia",          label: "IA",          icon: "" },
  { value: "digest",      label: "Digest",      icon: "" },
  { value: "cliente",     label: "Cliente",     icon: "" },
  { value: "tendencia",   label: "Tendência",   icon: "" },
  { value: "concorrente", label: "Concorrente", icon: "" },
];
function origemInfo(v: string | null | undefined) {
  return ORIGENS.find(o => o.value === v) ?? null;
}

// HU-069: potencial da ideia
const POTENCIAIS = [
  { value: "baixo", label: "Baixo", color: "#9CA3AF", bg: "#F3F4F6" },
  { value: "medio", label: "Médio", color: "#2563EB", bg: "#DBEAFE" },
  { value: "alto",  label: "Alto",  color: "#059669", bg: "#D1FAE5" },
  { value: "viral", label: "Viral", color: "#EC4899", bg: "#FDE8F5" },
];
function potencialInfo(v: string | null | undefined) {
  return POTENCIAIS.find(p => p.value === v) ?? null;
}

const KPI_MACROS = [
  { value: "autoridade",  label: "Autoridade de Marca" },
  { value: "alcance",     label: "Ampliar Alcance" },
  { value: "engajamento", label: "Engajamento Qualificado" },
  { value: "leads",       label: "Geração de Leads" },
  { value: "retencao",    label: "Retenção de Clientes" },
  { value: "fiscal",      label: "Oportunidade Fiscal" },
];

const INPUT_CLS = "w-full border border-[#E7E5E0] rounded-lg px-3 py-2.5 text-sm text-[#0A0A0A] placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#D6D3CD] focus:border-[#0A0A0A] transition bg-white";

interface Ideia {
  id: number;
  clientId: number;
  titulo: string;
  descricao?: string | null;
  linhaEditorial?: string | null;
  rede?: string | null;
  origem?: string | null;
  gancho?: string | null;
  potencial?: string | null;
  kpiMacro?: string | null;
  tags: string[];
  status: string;
  votos: number;
  pautaId?: number | null;
  createdAt: number;
  updatedAt: number;
  createdBy?: string | null;
}

// ─── NovaIdeiaModal ───────────────────────────────────────────────────────────

function NovaIdeiaModal({
  clientId,
  ideia,
  onSave,
  onClose,
}: {
  clientId: number;
  ideia?: Ideia | null;
  onSave: () => void;
  onClose: () => void;
}) {
  const [titulo, setTitulo]               = useState(ideia?.titulo ?? "");
  const [descricao, setDescricao]         = useState(ideia?.descricao ?? "");
  const [linhaEditorial, setLinhaEditorial] = useState(ideia?.linhaEditorial ?? "");
  const [rede, setRede]                   = useState<Rede | "">((ideia?.rede as Rede) ?? "");
  const [origem, setOrigem]               = useState(ideia?.origem ?? "manual");
  const [gancho, setGancho]               = useState(ideia?.gancho ?? "");
  const [potencial, setPotencial]         = useState(ideia?.potencial ?? "");
  const [kpiMacro, setKpiMacro]           = useState(ideia?.kpiMacro ?? "");
  const [tags, setTags]                   = useState<string[]>(ideia?.tags ?? []);
  const [tagInput, setTagInput]           = useState("");

  const createMutation = trpc.ideias.create.useMutation({ onSuccess: () => { onSave(); onClose(); } });
  const updateMutation = trpc.ideias.update.useMutation({ onSuccess: () => { onSave(); onClose(); } });
  const isPending = createMutation.isPending || updateMutation.isPending;

  function addTag() {
    const t = tagInput.trim().replace(/^#/, "");
    if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput("");
  }

  function handleSave() {
    if (!titulo.trim() || isPending) return;
    if (ideia) {
      updateMutation.mutate({
        id: ideia.id,
        titulo: titulo.trim(),
        descricao: descricao.trim() || null,
        linhaEditorial: linhaEditorial.trim() || null,
        rede: rede || null,
        origem: (origem || null) as any,
        gancho: gancho.trim() || null,
        potencial: (potencial || null) as any,
        kpiMacro: kpiMacro || null,
        tags,
      });
    } else {
      createMutation.mutate({
        clientId,
        titulo: titulo.trim(),
        descricao: descricao.trim() || undefined,
        linhaEditorial: linhaEditorial.trim() || undefined,
        rede: rede || undefined,
        origem: (origem || undefined) as any,
        gancho: gancho.trim() || undefined,
        potencial: (potencial || undefined) as any,
        kpiMacro: kpiMacro || undefined,
        tags,
      });
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-16 bottom-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[90vh] z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ border: "1px solid #E7E5E0" }}>
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: "1px solid #E7E5E0" }}>
          <h2 className="font-bold text-base text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>
            {ideia ? "Editar ideia" : "Nova ideia"}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Título da ideia *</label>
            <input autoFocus type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleSave(); }}
              placeholder="Qual é a ideia?" className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Descrição</label>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)}
              placeholder="Detalhe a ideia, contexto, referências…" rows={4}
              className={INPUT_CLS + " resize-none"} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Linha editorial</label>
            <div className="flex flex-wrap gap-2">
              {LINHAS_EDITORIAIS.map(l => {
                const isActive = linhaEditorial.startsWith(l.value.split(" ")[0]);
                return (
                  <button key={l.value} type="button"
                    onClick={() => setLinhaEditorial(isActive ? "" : l.value)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all"
                    style={{
                      background: isActive ? l.bg : "white",
                      borderColor: isActive ? l.color : "#E7E5E0",
                      color: isActive ? l.color : "#888",
                    }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                    {l.value}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Rede sugerida</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(REDE_INFO) as Rede[]).map(r => {
                const isActive = rede === r;
                return (
                  <button key={r} type="button"
                    onClick={() => setRede(isActive ? "" : r)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all"
                    style={{
                      background: isActive ? "#EDEBE6" : "white",
                      borderColor: isActive ? "#0A0A0A" : "#E7E5E0",
                      color: isActive ? "#0A0A0A" : "#888",
                    }}>
                    <span>{REDE_INFO[r].icon}</span>
                    {REDE_INFO[r].label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* HU-069: origem */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Origem</label>
            <div className="flex flex-wrap gap-2">
              {ORIGENS.map(o => {
                const isActive = origem === o.value;
                return (
                  <button key={o.value} type="button"
                    onClick={() => setOrigem(o.value)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all"
                    style={{
                      background: isActive ? "#EDEBE6" : "white",
                      borderColor: isActive ? "#0A0A0A" : "#E7E5E0",
                      color: isActive ? "#0A0A0A" : "#888",
                    }}>
                    <span>{o.icon}</span>{o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* HU-069: gancho */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Gancho</label>
            <input type="text" value={gancho} onChange={e => setGancho(e.target.value)}
              placeholder="Como o conteúdo prende a atenção?" className={INPUT_CLS} />
          </div>

          {/* HU-069: potencial */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Potencial</label>
            <div className="flex gap-2">
              {POTENCIAIS.map(p => {
                const isActive = potencial === p.value;
                return (
                  <button key={p.value} type="button"
                    onClick={() => setPotencial(isActive ? "" : p.value)}
                    className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all"
                    style={{
                      background: isActive ? p.bg : "white",
                      borderColor: isActive ? p.color : "#E7E5E0",
                      color: isActive ? p.color : "#888",
                    }}>
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* HU-069: KPI macro */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">KPI macro</label>
            <div className="flex flex-wrap gap-2">
              {KPI_MACROS.map(k => {
                const isActive = kpiMacro === k.value;
                return (
                  <button key={k.value} type="button"
                    onClick={() => setKpiMacro(isActive ? "" : k.value)}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all"
                    style={{
                      background: isActive ? "#FEF3C7" : "white",
                      borderColor: isActive ? "#52525B" : "#E7E5E0",
                      color: isActive ? "#92400E" : "#888",
                    }}>
                    {k.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* HU-069: tags */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Tags</label>
            <div className="flex gap-2">
              <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                placeholder="#tag" className={INPUT_CLS} />
              <button type="button" onClick={addTag}
                className="px-3 py-2 rounded-lg text-sm font-bold text-white" style={{ background: "#0A0A0A" }}>+</button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full text-[#0A0A0A] font-medium" style={{ background: "#F5F4F1" }}>
                    #{t}
                    <button onClick={() => setTags(prev => prev.filter(x => x !== t))} className="text-[#0A0A0A]/50 hover:text-[#0A0A0A]">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {(createMutation.error || updateMutation.error) && (
          <p className="px-6 py-2 text-red-500 text-sm">{(createMutation.error || updateMutation.error)?.message}</p>
        )}

        <div className="flex gap-3 px-6 py-4 shrink-0" style={{ borderTop: "1px solid #E7E5E0" }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[#E7E5E0] text-[#0A0A0A] text-sm font-semibold hover:bg-[#F5F4F1] transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={!titulo.trim() || isPending}
            className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-40"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}>
            {isPending ? "Salvando…" : ideia ? "Salvar alterações" : "Salvar ideia"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── IdeiaCard ────────────────────────────────────────────────────────────────

function IdeiaCard({
  ideia,
  onEdit,
  onVote,
  onStatus,
  onConvert,
  onDelete,
}: {
  ideia: Ideia;
  onEdit: (i: Ideia) => void;
  onVote: (id: number, delta: number) => void;
  onStatus: (id: number, status: string) => void;
  onConvert: (id: number) => void;
  onDelete: (id: number) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const lc = linhaColor(ideia.linhaEditorial);
  const st = STATUS_INFO[ideia.status] ?? STATUS_INFO.nova;

  return (
    <div className="bg-white rounded-xl border border-[#E7E5E0] p-4 shadow-sm hover:shadow-md hover:border-[#D6D3CD] transition-all relative group">
      <div className="flex items-start gap-3">
        {/* Votos */}
        <div className="flex flex-col items-center shrink-0">
          <button onClick={() => onVote(ideia.id, 1)}
            className="text-zinc-300 hover:text-[#0A0A0A] transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <span className="text-sm font-bold text-[#0A0A0A]">{ideia.votos}</span>
          <button onClick={() => onVote(ideia.id, -1)}
            className="text-zinc-300 hover:text-zinc-500 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[#0A0A0A] text-sm font-semibold leading-snug" style={{ fontFamily: "Poppins, sans-serif" }}>
              {ideia.titulo}
            </p>
            <div className="relative shrink-0">
              <button onClick={() => setMenuOpen(o => !o)}
                className="w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#F5F4F1]"
                style={{ color: "#0A0A0A" }}>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
                </svg>
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-7 z-20 bg-white rounded-xl border border-[#E7E5E0] shadow-lg py-1 w-44 text-xs">
                    <button onClick={() => { onEdit(ideia); setMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-[#F5F4F1] text-[#0A0A0A] font-medium">
                      Editar
                    </button>
                    <div className="border-t border-[#E7E5E0] my-1" />
                    {STATUS_ORDER.filter(s => s !== ideia.status && s !== "convertida").map(s => (
                      <button key={s} onClick={() => { onStatus(ideia.id, s); setMenuOpen(false); }}
                        className="w-full text-left px-3 py-2 hover:bg-[#F5F4F1] text-zinc-600 font-medium">
                        → {STATUS_INFO[s].label}
                      </button>
                    ))}
                    <div className="border-t border-[#E7E5E0] my-1" />
                    <button onClick={() => { onDelete(ideia.id); setMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-500 font-medium">
                      Excluir
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {ideia.descricao && (
            <p className="text-zinc-500 text-xs mt-1.5 line-clamp-2 leading-relaxed">{ideia.descricao}</p>
          )}

          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
            <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: st.bg, color: st.color }}>
              {st.label}
            </span>
            {ideia.linhaEditorial && (
              <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: lc.bg, color: lc.color }}>
                {ideia.linhaEditorial}
              </span>
            )}
            {ideia.rede && (
              <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-[#F5F4F1] text-[#0A0A0A]">
                {REDE_INFO[ideia.rede as Rede]?.icon} {REDE_INFO[ideia.rede as Rede]?.label}
              </span>
            )}
            {potencialInfo(ideia.potencial) && (
              <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                style={{ background: potencialInfo(ideia.potencial)!.bg, color: potencialInfo(ideia.potencial)!.color }}>
                ⚡ {potencialInfo(ideia.potencial)!.label}
              </span>
            )}
            {origemInfo(ideia.origem) && (
              <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-zinc-100 text-zinc-500">
                {origemInfo(ideia.origem)!.icon} {origemInfo(ideia.origem)!.label}
              </span>
            )}
          </div>

          {/* Ação converter */}
          {ideia.status !== "convertida" && ideia.status !== "descartada" && (
            <button onClick={() => onConvert(ideia.id)}
              className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all hover:bg-[#EDEBE6]"
              style={{ borderColor: "#0A0A0A", color: "#0A0A0A" }}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              Converter em pauta
            </button>
          )}
          {ideia.status === "convertida" && (
            <p className="mt-3 text-xs text-[#0891B2] font-medium flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Convertida em pauta
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── HU-070: geração de ideias por IA ─────────────────────────────────────────

function GerarIAModal({
  clientId, onSaved, onClose,
}: {
  clientId: number;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [linhaEditorial, setLinhaEditorial] = useState("");
  const [rede, setRede] = useState<Rede | "">("");
  const [tema, setTema] = useState("");
  const [sugestoes, setSugestoes] = useState<{ titulo: string; gancho: string }[]>([]);
  const [adicionadas, setAdicionadas] = useState<Set<number>>(new Set());

  const gerarMutation = trpc.ideias.gerarComIA.useMutation({
    onSuccess: (res: any) => { setSugestoes(res ?? []); setAdicionadas(new Set()); },
  });
  const createMutation = trpc.ideias.create.useMutation();

  function gerar() {
    if (gerarMutation.isPending) return;
    setSugestoes([]);
    gerarMutation.mutate({
      linhaEditorial: linhaEditorial || undefined,
      rede: rede || undefined,
      tema: tema.trim() || undefined,
    });
  }

  async function adicionar(idx: number) {
    const s = sugestoes[idx];
    if (!s || adicionadas.has(idx)) return;
    await createMutation.mutateAsync({
      clientId,
      titulo: s.titulo,
      descricao: s.gancho || undefined,
      gancho: s.gancho || undefined,
      linhaEditorial: linhaEditorial || undefined,
      rede: (rede || undefined) as any,
      origem: "ia",
    });
    setAdicionadas(prev => new Set(prev).add(idx));
    onSaved();
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-12 bottom-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[90vh] z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ border: "1px solid #E7E5E0" }}>
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: "1px solid #E7E5E0" }}>
          <h2 className="font-bold text-base text-[#0A0A0A] flex items-center gap-2" style={{ fontFamily: "Poppins, sans-serif" }}>
            <span></span> Gerar ideias com IA
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Linha editorial</label>
            <div className="flex flex-wrap gap-2">
              {LINHAS_EDITORIAIS.map(l => {
                const isActive = linhaEditorial.startsWith(l.value.split(" ")[0]);
                return (
                  <button key={l.value} type="button"
                    onClick={() => setLinhaEditorial(isActive ? "" : l.value)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all"
                    style={{ background: isActive ? l.bg : "white", borderColor: isActive ? l.color : "#E7E5E0", color: isActive ? l.color : "#888" }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                    {l.value}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Rede</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(REDE_INFO) as Rede[]).map(r => {
                const isActive = rede === r;
                return (
                  <button key={r} type="button" onClick={() => setRede(isActive ? "" : r)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all"
                    style={{ background: isActive ? "#EDEBE6" : "white", borderColor: isActive ? "#0A0A0A" : "#E7E5E0", color: isActive ? "#0A0A0A" : "#888" }}>
                    <span>{REDE_INFO[r].icon}</span>{REDE_INFO[r].label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Tema / assunto (opcional)</label>
            <input type="text" value={tema} onChange={e => setTema(e.target.value)}
              placeholder="Ex: reforma tributária, dicas de gestão…" className={INPUT_CLS} />
          </div>

          <button onClick={gerar} disabled={gerarMutation.isPending}
            className="w-full py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-50"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}>
            {gerarMutation.isPending ? "Gerando ideias…" : sugestoes.length > 0 ? "Gerar novamente" : "Gerar ideias"}
          </button>

          {gerarMutation.error && (
            <p className="text-xs text-red-500">{gerarMutation.error.message}</p>
          )}

          {sugestoes.length > 0 && (
            <div className="space-y-2 pt-1">
              <p className="text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest">Sugestões</p>
              {sugestoes.map((s, idx) => (
                <div key={idx} className="rounded-xl border border-[#E7E5E0] p-3 bg-[#FAFAFE]">
                  <p className="text-sm font-semibold text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>{s.titulo}</p>
                  {s.gancho && <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{s.gancho}</p>}
                  <button onClick={() => adicionar(idx)} disabled={adicionadas.has(idx)}
                    className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all disabled:opacity-60"
                    style={{ borderColor: adicionadas.has(idx) ? "#10B981" : "#0A0A0A", color: adicionadas.has(idx) ? "#10B981" : "#0A0A0A" }}>
                    {adicionadas.has(idx) ? "✓ Adicionada" : "+ Adicionar ao banco"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 shrink-0" style={{ borderTop: "1px solid #E7E5E0" }}>
          <button onClick={onClose}
            className="w-full py-2.5 rounded-lg border border-[#E7E5E0] text-[#0A0A0A] text-sm font-semibold hover:bg-[#F5F4F1] transition-colors">
            Concluir
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Página Banco de Ideias ───────────────────────────────────────────────────

export default function BancoIdeias() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;
  const [, navigate] = useLocation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando]   = useState<Ideia | null>(null);
  const [gerarIAOpen, setGerarIAOpen] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");
  const [filtroOrigem, setFiltroOrigem] = useState<string>("todas");
  const [filtroLinha, setFiltroLinha]   = useState<string>("todas");

  const { data: ideias = [], isLoading, refetch } = trpc.ideias.list.useQuery(
    { clientId },
    { enabled: !!clientId }
  );

  const voteMutation    = trpc.ideias.vote.useMutation({ onSuccess: () => refetch() });
  const updateMutation  = trpc.ideias.update.useMutation({ onSuccess: () => refetch() });
  const deleteMutation  = trpc.ideias.delete.useMutation({ onSuccess: () => refetch() });
  const convertMutation = trpc.ideias.converterEmPauta.useMutation({
    onSuccess: () => refetch(),
  });

  const linhasDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const i of ideias) if (i.linhaEditorial) set.add(i.linhaEditorial);
    return Array.from(set).sort();
  }, [ideias]);

  // Nome da coluna onde a pauta nasce, no quadro do cliente (out/26).
  const { quadro: quadroIdeias } = useQuadro();
  const rotuloEtapaIni = rotuloEtapa(etapaInicial(quadroIdeias), quadroIdeias);

  const filtradas = useMemo(() => {
    return ideias.filter(i => {
      if (filtroStatus !== "todos" && i.status !== filtroStatus) return false;
      if (filtroOrigem !== "todas" && (i.origem ?? "manual") !== filtroOrigem) return false;
      if (filtroLinha !== "todas" && i.linhaEditorial !== filtroLinha) return false;
      return true;
    });
  }, [ideias, filtroStatus, filtroOrigem, filtroLinha]);

  const contagem = useMemo(() => {
    const c: Record<string, number> = {};
    for (const i of ideias) c[i.status] = (c[i.status] ?? 0) + 1;
    return c;
  }, [ideias]);

  function handleConvert(id: number) {
    if (!confirm(`Converter esta ideia em pauta? Ela será criada na coluna ${rotuloEtapaIni}.`)) return;
    convertMutation.mutate({ id }, {
      onSuccess: (res: any) => {
        refetch();
        if (res?.pautaId && confirm("Pauta criada! Deseja abrir o quadro de Pautas?")) {
          navigate("/pautas");
        }
      },
    });
  }

  function handleDelete(id: number) {
    if (confirm("Excluir esta ideia?")) deleteMutation.mutate({ id });
  }

  return (
    <div className="h-full flex flex-col bg-[#F5F4F1]">
      {/* Header */}
      <div className="bg-white border-b border-[#E7E5E0] px-6 py-4 shrink-0">
        <div className="flex items-center gap-4 flex-wrap gap-y-3">
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-lg text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>
              Banco de Ideias
            </h1>
            <p className="text-zinc-400 text-xs mt-0.5">
              {ideias.length} {ideias.length === 1 ? "ideia capturada" : "ideias capturadas"}
            </p>
          </div>

          {/* HU-070: gerar com IA */}
          <button
            onClick={() => setGerarIAOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border-2 transition-all hover:bg-[#EDEBE6] shrink-0"
            style={{ borderColor: "#0A0A0A", color: "#0A0A0A", fontFamily: "Poppins, sans-serif" }}
          >
            Gerar com IA
          </button>

          <button
            onClick={() => { setEditando(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90 shrink-0"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Nova ideia
          </button>
        </div>

        {/* Filtros por status */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <button
            onClick={() => setFiltroStatus("todos")}
            className={cn("px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border")}
            style={{
              background: filtroStatus === "todos" ? "#0A0A0A" : "white",
              borderColor: filtroStatus === "todos" ? "#0A0A0A" : "#E7E5E0",
              color: filtroStatus === "todos" ? "white" : "#888",
            }}
          >
            Todas ({ideias.length})
          </button>
          {STATUS_ORDER.map(s => {
            const info = STATUS_INFO[s];
            const isActive = filtroStatus === s;
            return (
              <button
                key={s}
                onClick={() => setFiltroStatus(s)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border-2"
                style={{
                  background: isActive ? info.bg : "white",
                  borderColor: isActive ? info.color : "#E7E5E0",
                  color: isActive ? info.color : "#888",
                }}
              >
                {info.label} ({contagem[s] ?? 0})
              </button>
            );
          })}
        </div>

        {/* HU-068: filtros por origem e linha editorial */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <select
            value={filtroOrigem}
            onChange={e => setFiltroOrigem(e.target.value)}
            className="text-xs border border-[#E7E5E0] rounded-lg px-2.5 py-1.5 text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] bg-white"
          >
            <option value="todas">Origem (todas)</option>
            {ORIGENS.map(o => <option key={o.value} value={o.value}>{o.icon} {o.label}</option>)}
          </select>
          {linhasDisponiveis.length > 0 && (
            <select
              value={filtroLinha}
              onChange={e => setFiltroLinha(e.target.value)}
              className="text-xs border border-[#E7E5E0] rounded-lg px-2.5 py-1.5 text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] bg-white"
            >
              <option value="todas">Linha editorial (todas)</option>
              {linhasDisponiveis.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          {(filtroOrigem !== "todas" || filtroLinha !== "todas") && (
            <button
              onClick={() => { setFiltroOrigem("todas"); setFiltroLinha("todas"); }}
              className="text-xs text-[#0A0A0A] underline underline-offset-2 hover:opacity-70"
            >
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-[#E7E5E0] border-t-[#0A0A0A] animate-spin" />
        </div>
      ) : filtradas.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-zinc-400">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "#EDEBE6" }}>
            <svg className="w-8 h-8 text-[#0A0A0A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <p className="text-sm font-medium">
            {filtroStatus === "todos" ? "Nenhuma ideia ainda" : "Nenhuma ideia nesse status"}
          </p>
          {filtroStatus === "todos" && (
            <button
              onClick={() => { setEditando(null); setModalOpen(true); }}
              className="text-sm font-semibold text-[#0A0A0A] underline underline-offset-2"
            >
              Capturar a primeira ideia
            </button>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {filtradas.map(ideia => (
              <IdeiaCard
                key={ideia.id}
                ideia={ideia as Ideia}
                onEdit={i => { setEditando(i); setModalOpen(true); }}
                onVote={(id, delta) => voteMutation.mutate({ id, delta })}
                onStatus={(id, status) => updateMutation.mutate({ id, status: status as any })}
                onConvert={handleConvert}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}

      {modalOpen && (
        <NovaIdeiaModal
          clientId={clientId}
          ideia={editando}
          onSave={() => refetch()}
          onClose={() => { setModalOpen(false); setEditando(null); }}
        />
      )}

      {gerarIAOpen && (
        <GerarIAModal
          clientId={clientId}
          onSaved={() => refetch()}
          onClose={() => setGerarIAOpen(false)}
        />
      )}
    </div>
  );
}
